'use client';
import { Landscape_Description } from '@/components/ui/catalog/landscape/description';
import { Landscape_Display } from '../landscape/LandscapeDisplay';
import { Landscape_Immersive } from '../landscape/LandscapeImmersive';
import type {
  Physicalartwork,
  LandscapeDisplay,
  LandscapeImmersive,
  LandscapeDescription,
  LandscapeChapterHero,
  LandscapeChapter,
  LandscapeProcess,
  LandscapeSketchbook,
  LandscapeVisionDisplay,
  LandscapeDocumentary,
  LandscapeRenders,
  LandscapePress,
  LandscapeGallery,
} from '@/types';
import { Landscape_ChapterHero } from '../landscape/LandscapeChapterHero';
import { Landscape_Chapter } from '../landscape/LandscapeChapter';
import { Landscape_Process } from '../landscape/LandscapeProcess';
import { Landscape_Sketchbook } from '../landscape/LandscapeSketchbook';
import { Common_HeroSection } from '../landscape/CommonHeroSection';
import { EntryInformation, EntryPhotos, readLayout, type EntrySpec } from '../landscape/EntryInformation';
import { Landscape_Documentary } from '../landscape/LandscapeDocumentary';
import { Landscape_Renders } from '../landscape/LandscapeRender';
import { Landscape_Gallery } from '../landscape/LandscapeGallery';
import { Landscape_Press } from '../landscape/LandscapePress';

// Layout types supported by the GetLayout component
type SupportedLayout =
  | LandscapeDisplay
  | LandscapeImmersive
  | LandscapeVisionDisplay
  | LandscapeDescription
  | LandscapeChapterHero
  | LandscapeChapter
  | LandscapeProcess
  | LandscapeSketchbook
  | LandscapeDocumentary
  | LandscapeRenders
  | LandscapeGallery
  | LandscapePress;

interface PhysicalProps {
  detail: Physicalartwork;
}

export default function PhysicalDetailPage({ detail }: PhysicalProps) {
  // Etiketleri dönüştür
  const tags: string[] = [];

  // Etiketleri ekle
  if (detail.tags && Array.isArray(detail.tags)) {
    detail.tags.forEach(tag => {
      if (typeof tag === 'number') {
        tags.push(String(tag));
      } else if (tag && typeof tag === 'object' && 'title' in tag) {
        tags.push(tag.title);
      }
    });
  }

  // What the entry knows about itself. Shown by the description block where the layout has
  // one, and by the page itself where it does not, so no entry is left as a bare title.
  const specs: EntrySpec[] = [];
  // medium
  if (detail.medium) {
    specs.push({
      label: 'Medium',
      specifications: [{ value: detail.medium }],
    });
  }
  // dimensions
  if (detail.dimensions) {
    specs.push({
      label: 'Dimensions',
      specifications: [{ value: detail.dimensions }],
    });
  }
  // location
  if (detail.location) {
    specs.push({
      label: 'Location',
      specifications: [{ value: detail.location }],
    });
  }

  // materials array
  if (detail.materials && Array.isArray(detail.materials) && detail.materials.length > 0) {
    specs.push({
      label: 'Materials',
      specifications: detail.materials.map(material => ({
        value: material.material,
      })),
    });
  }
  // shippingDetails
  if (detail.shippingDetails) {
    if (detail.shippingDetails.availability) {
      specs.push({
        label: 'Availability',
        specifications: [{ value: detail.shippingDetails.availability }],
      });
    }
    if (detail.shippingDetails.insurance) {
      specs.push({
        label: 'Insurance',
        specifications: [{ value: detail.shippingDetails.insurance }],
      });
    }
  }
  // year
  if (detail.year) {
    specs.push({
      label: 'Year',
      specifications: [{ value: detail.year, light: true }],
    });
  }
  // collection
  if (detail.collection) {
    if (typeof detail.collection !== 'number') {
      specs.push({
        label: 'Collection',
        specifications: [
          { value: detail.collection.name, url: `/catalog/collections/${detail.collection.slug}`, light: true },
        ],
      });
    }
  }
  // artwork
  if (detail.artwork) {
    const artworks = [];
    for (const artwork of detail.artwork) {
      if (typeof artwork !== 'number') {
        artworks.push({ value: artwork.name, url: `/catalog/artworks/${artwork.id}`, light: true });
      }
    }
    if (artworks.length > 0) {
      specs.push({
        label: 'Artwork' + (artworks.length > 1 ? 's' : ''),
        specifications: artworks,
      });
    }
  }

  // if (detail.type) {
  //   specs.push({
  //     label: 'Type',
  //     specifications: [{ value: detail.type }],
  //   });
  // }
  // // typeof detail.status
  // if (typeof detail.status === 'object') {
  //   specs.push({
  //     label: 'Status',
  //     specifications: [{ value: detail.status.title }],
  //   });
  // }
  // // date dd/mm/yyyy
  // if (detail.date) {
  //   const date = new Date(detail.date);
  //   const day = date.getDate();
  //   const month = date.getMonth() + 1;
  //   const year = date.getFullYear();
  //   specs.push({
  //     label: 'Date',
  //     specifications: [{ value: `${day}/${month}/${year}` }],
  //   });
  // }

  // if (detail.highlights) {
  //   specs.push({
  //     label: 'Highlights',
  //     specifications: detail.highlights.map(highlight => ({ value: highlight.highlight })),
  //   });
  // }

  // if (detail.projectUrl) {
  //   specs.push({
  //     label: 'Project URL',
  //     specifications: [
  //       {
  //         value: new URL(detail.projectUrl).hostname.replace(/^www\./, ''),
  //         url: detail.projectUrl,
  //         light: true,
  //       },
  //     ],
  //   });
  // }
  // // partners
  // if (detail.partners) {
  //   specs.push({
  //     label: 'Partners',
  //     specifications: detail.partners.map(partner => ({ value: `${partner.name} - ${partner.role}` })),
  //   });
  // }
  const page = readLayout(detail.layout);

  const GetLayout = ({
    layout,
  }: {
    layout:
      | LandscapeDisplay
      | LandscapeImmersive
      | LandscapeVisionDisplay
      | LandscapeDescription
      | LandscapeChapterHero
      | LandscapeChapter
      | LandscapeProcess
      | LandscapeSketchbook
      | LandscapeDocumentary
      | LandscapeRenders
      | LandscapeGallery
      | LandscapePress;
  }) => {
    switch (layout.blockType) {
      case 'LandscapeDisplay':
        return <Landscape_Display layout={layout} />;
      case 'LandscapeImmersive':
        return <Landscape_Immersive layout={layout} />;
      case 'LandscapeChapterHero':
        return <Landscape_ChapterHero layout={layout} />;
      case 'LandscapeChapter':
        return <Landscape_Chapter layout={layout} />;
      case 'LandscapeProcess':
        return <Landscape_Process layout={layout} />;
      case 'LandscapeSketchbook':
        return <Landscape_Sketchbook layout={layout} />;
      case 'LandscapeDocumentary':
        return <Landscape_Documentary layout={layout} />;
      case 'LandscapeRenders':
        return <Landscape_Renders layout={layout} />;
      case 'LandscapePress':
        return <Landscape_Press layout={layout} />;
      case 'LandscapeGallery':
        return <Landscape_Gallery layout={layout} />;
      case 'LandscapeDescription':
        return <Landscape_Description layout={layout} specs={specs} />;
      default:
        return null; // Eşleşme yoksa null döndür
    }
  };

  // Content area - ensure overflow works with parent container
  return (
    <div
      className="w-full bg-black relative h-full overflow-y-auto"
      style={{ scrollbarWidth: 'none' }}
      id="landscape-content"
    >
      {/* Hero Section */}
      <Common_HeroSection
        title={detail.title}
        description={detail.description}
        leftTop={detail.medium}
        rightTop={detail.location}
        tags={tags}
      />

      {/* An entry with no description block still shows what it knows. */}
      {page.describes ? null : <EntryInformation specs={specs} />}

      {/* Installation Sections */}
      {/* TODO: type properly - Physicalartwork.layout includes LandscapeExhibition not handled by GetLayout */}
      {detail.layout.map((lay, index) => {
        const layout = lay as unknown as SupportedLayout;
        return <GetLayout key={index} layout={layout} />;
      })}
      {/* The entry's own photographs, where the page shows no pictures or film of its own. */}
      {page.showsMedia ? null : <EntryPhotos photos={detail.photos} title={detail.title} />}
    </div>
  );
}
